import { useParams } from 'react-router';
import { useHistory, useLocation } from 'react-router-dom';
import { useState, createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { subYears, addYears } from 'date-fns';

import MeasurementEditService from 'app/admin/measurement/service/MeasurementEditService';
import MeasurementTableService from 'app/admin/measurement/service/MeasurementTableService';
import ModelValidationEditService from 'app/admin/modelValidation/service/ModelValidationEditService';
import ModelValidationLogTableService from 'app/admin/modelValidationLog/service/ModelValidationLogTableService';
import UnitOptimizerEditService from 'app/admin/unitOptimizer/service/UnitOptimizerEditService';
import UnitOptimizerLogTableService from 'app/admin/unitOptimizerLog/service/UnitOptimizerLogTableService';
import OptimizationPipelineLogTableService from 'app/admin/optimizationPipelineLog/service/OptimizationPipelineLogTableService';
import OptimizationPipelineEditService from 'app/admin/optimizationPipelineV2/service/OptimizationPipelineEditService';
import PiWriterService from 'app/admin/piWriter/service/PiWriterService';
import EdtDynamicCheckEditService from 'app/admin/edtDynamicCheck/service/EdtDynamicCheckEditService';
import EdtDynamicCheckLogTableService from 'app/admin/edtDynamicCheckLog/service/EdtDynamicCheckLogTableService';

import { getTsCacheKey } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/dateTime';

export const OptimizationPipelineContext = createContext(null);

export const useOptimizationPipeline = () => { return useContext(OptimizationPipelineContext); };

const EMPTY_RESOURCE = {
    data: null,
    loading: false,
    loaded: false,
    error: null
};

function getSearchParamTs(search) {
    const params = new URLSearchParams(search || '');
    return params.get('ts') || null;
}

function findMatchingTimestamp(timestamps, value) {
    if (!Array.isArray(timestamps) || !value) return null;

    const targetKey = getTsCacheKey(value);
    if (!targetKey) return null;

    return timestamps.find((item) => getTsCacheKey(item) === targetKey) || null;
}

export default function OptimizationPipelineProvider(props) {
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();

    const [sidebar, setSidebar] = useState(null);
    const [popup, setPopup] = useState(null);

    const [ts, setTs] = useState(null);

    const [timestamps, setTimestamps] = useState(null);

    const [optimizationPipelineData, setOptimizationPipelineData] = useState(null);
    const [optimizationPipelineDetails, setOptimizationPipelineDetails] = useState(null);
    const [optimizationPipelineDetailsLoading, setOptimizationPipelineDetailsLoading] = useState(false);
    const [optimizationPipelineDetailsLoaded, setOptimizationPipelineDetailsLoaded] = useState(false);

    const [onDemandResources, setOnDemandResources] = useState({});

    const optimizationPipelineDetailsCacheRef = useRef({});
    const optimizationPipelineDetailsRequestRef = useRef(0);
    const onDemandRequestRefs = useRef({});
    const lastSyncedSearchTsRef = useRef(null);

    function openSidebar(nextSidebar) {
        if (!nextSidebar) return;
        setSidebar(nextSidebar);
    }

    function closeSidebar() {
        setSidebar(null);
    }

    function openPopup(nextPopup) {
        if (!nextPopup) return;
        setPopup(nextPopup);
    }

    function closePopup() {
        setPopup(null);
    }

    const getOnDemandResource = useCallback((resourceKey) => {
        return onDemandResources[resourceKey] || EMPTY_RESOURCE;
    }, [onDemandResources]);

    const ensureOnDemandResource = useCallback((resourceKey) => {
        if (!optimizationPipelineData) return Promise.resolve(null);

        const currentResource = onDemandResources[resourceKey] || EMPTY_RESOURCE;

        if (currentResource.loading) return Promise.resolve(currentResource.data);
        if (currentResource.loaded) return Promise.resolve(currentResource.data);

        let loader = null;

        if (resourceKey === 'mergedMeasurementDataList') {
            if (!optimizationPipelineData.mergedMeasurement) return Promise.resolve(null);

            loader = () => {
                return MeasurementTableService.getData(
                    optimizationPipelineData.mergedMeasurement,
                    null,
                    null
                );
            };
        }

        if (resourceKey === 'mergedMeasurementData') {
            if (!optimizationPipelineData.mergedMeasurement) return Promise.resolve(null);

            loader = () => {
                return MeasurementEditService.getData(optimizationPipelineData.mergedMeasurement);
            };
        }

        if (resourceKey === 'cleanMeasurementData') {
            if (!optimizationPipelineData.cleanMeasurement) return Promise.resolve(null);

            loader = () => {
                return MeasurementEditService.getData(optimizationPipelineData.cleanMeasurement);
            };
        }

        if (resourceKey === 'modelValidationData') {
            if (!optimizationPipelineData.modelValidation) return Promise.resolve(null);

            loader = () => {
                return ModelValidationEditService.getData(optimizationPipelineData.modelValidation);
            };
        }

        if (resourceKey === 'modelValidationLogDataList') {
            if (!optimizationPipelineData.modelValidation) return Promise.resolve(null);

            loader = () => {
                return ModelValidationEditService.getData(optimizationPipelineData.modelValidation)
                    .then((modelValidationData) => {
                        if (!modelValidationData?.modelValidationLog) return null;

                        return ModelValidationLogTableService.getData(
                            modelValidationData.modelValidationLog,
                            null,
                            null
                        );
                    });
            };
        }

        if (resourceKey === 'unitOptimizerData') {
            if (!optimizationPipelineData.unitOptimizer) return Promise.resolve(null);

            loader = () => {
                return UnitOptimizerEditService.getData(optimizationPipelineData.unitOptimizer);
            };
        }

        if (resourceKey === 'unitOptimizerLogDataList') {
            if (!optimizationPipelineData.unitOptimizer) return Promise.resolve(null);

            loader = () => {
                return UnitOptimizerEditService.getData(optimizationPipelineData.unitOptimizer)
                    .then((unitOptimizerData) => {
                        if (!unitOptimizerData?.unitOptimizerLog) return null;

                        return UnitOptimizerLogTableService.getData(
                            unitOptimizerData.unitOptimizerLog,
                            null,
                            null
                        );
                    });
            };
        }

        if (resourceKey === 'piWriterInputAttributeNames') {
            if (!optimizationPipelineData.piWriter) return Promise.resolve(null);

            loader = () => {
                return PiWriterService.getInputAttributeNames(
                    optimizationPipelineData.piWriter
                );
            };
        }

        if (resourceKey === 'edtDynamicCheckLogData') {
            if (!optimizationPipelineData.edtDynamicCheck || !ts) return Promise.resolve(null);

            loader = () => {
                return EdtDynamicCheckEditService.getData(optimizationPipelineData.edtDynamicCheck)
                    .then((edtDynamicCheckData) => {
                        if (!edtDynamicCheckData?.log) return null;

                        return EdtDynamicCheckLogTableService.getDataByTimestamp(
                            edtDynamicCheckData.log,
                            ts
                        );
                    });
            };
        }

        if (resourceKey?.startsWith?.('edtDynamicCheckTrajectoryData:')) {
            const runId = resourceKey.split(':')[1];

            if (!optimizationPipelineData.edtDynamicCheck || !runId) return Promise.resolve(null);

            loader = () => {
                return EdtDynamicCheckEditService.getData(optimizationPipelineData.edtDynamicCheck)
                    .then((edtDynamicCheckData) => {
                        if (!edtDynamicCheckData?.log) return null;

                        return EdtDynamicCheckLogTableService.getTrajectory(
                            edtDynamicCheckData.log,
                            runId
                        );
                    });
            };
        }

        if (!loader) return Promise.resolve(null);

        const nextRequestId = (onDemandRequestRefs.current[resourceKey] || 0) + 1;
        onDemandRequestRefs.current[resourceKey] = nextRequestId;

        setOnDemandResources((prev) => ({
            ...prev,
            [resourceKey]: {
                ...(prev[resourceKey] || EMPTY_RESOURCE),
                loading: true,
                loaded: false,
                error: null
            }
        }));

        return loader()
            .then((resp) => {
                if (onDemandRequestRefs.current[resourceKey] !== nextRequestId) return null;

                setOnDemandResources((prev) => ({
                    ...prev,
                    [resourceKey]: {
                        data: resp,
                        loading: false,
                        loaded: true,
                        error: null
                    }
                }));

                return resp;
            })
            .catch((error) => {
                if (onDemandRequestRefs.current[resourceKey] !== nextRequestId) return null;

                setOnDemandResources((prev) => ({
                    ...prev,
                    [resourceKey]: {
                        data: null,
                        loading: false,
                        loaded: true,
                        error: error || true
                    }
                }));

                return null;
            });
    }, [optimizationPipelineData, onDemandResources, ts]);

    useEffect(() => {
        setOnDemandResources((prev) => {
            const next = { ...prev };
            let changed = false;

            if (next.edtDynamicCheckLogData) {
                delete next.edtDynamicCheckLogData;
                changed = true;
            }

            Object.keys(next).forEach((resourceKey) => {
                if (resourceKey.startsWith('edtDynamicCheckTrajectoryData:')) {
                    delete next[resourceKey];
                    delete onDemandRequestRefs.current[resourceKey];
                    changed = true;
                }
            });

            return changed ? next : prev;
        });

        onDemandRequestRefs.current.edtDynamicCheckLogData = 0;
    }, [ts]);

    useEffect(() => {
        if (id && id !== 'new') {
            OptimizationPipelineEditService.getData(id).then(resp => {
                setOptimizationPipelineData(resp);
                setOptimizationPipelineDetails(null);
                setOptimizationPipelineDetailsLoading(false);
                setOptimizationPipelineDetailsLoaded(false);
                setOnDemandResources({});
                setSidebar(null);
                setPopup(null);
                setTs(null);
                setTimestamps(null);
                optimizationPipelineDetailsCacheRef.current = {};
                optimizationPipelineDetailsRequestRef.current = 0;
                onDemandRequestRefs.current = {};
                lastSyncedSearchTsRef.current = null;
            });
        } else {
            setOptimizationPipelineData(null);
            setOptimizationPipelineDetails(null);
            setOptimizationPipelineDetailsLoading(false);
            setOptimizationPipelineDetailsLoaded(false);
            setOnDemandResources({});
            setSidebar(null);
            setPopup(null);
            setTs(null);
            setTimestamps(null);
            optimizationPipelineDetailsCacheRef.current = {};
            optimizationPipelineDetailsRequestRef.current = 0;
            onDemandRequestRefs.current = {};
            lastSyncedSearchTsRef.current = null;
        }
    }, [id]);

    useEffect(() => {
        if (optimizationPipelineData?.optimizationPipelineLog) {
            OptimizationPipelineLogTableService.getTimestampsInDateInterval(
                optimizationPipelineData.optimizationPipelineLog,
                subYears(new Date(), 1),
                addYears(new Date(), 1)
            ).then(resp => {
                setTimestamps(resp.data);
            });
        } else {
            setTimestamps(null);
        }
    }, [optimizationPipelineData]);

    useEffect(() => {
        if (!Array.isArray(timestamps) || timestamps.length === 0) return;

        const rawSearchTs = getSearchParamTs(location.search);
        const matchedSearchTs = findMatchingTimestamp(timestamps, rawSearchTs);
        const matchedSearchTsKey = getTsCacheKey(matchedSearchTs);

        if (!matchedSearchTsKey) return;
        if (lastSyncedSearchTsRef.current === matchedSearchTsKey) return;

        const currentTsKey = getTsCacheKey(ts);

        if (currentTsKey === matchedSearchTsKey) {
            lastSyncedSearchTsRef.current = matchedSearchTsKey;
            return;
        }

        setTs(matchedSearchTs);
        lastSyncedSearchTsRef.current = matchedSearchTsKey;
    }, [location.search, timestamps, ts]);

    useEffect(() => {
        if (!Array.isArray(timestamps) || timestamps.length === 0) return;

        const matchedCurrentTs = findMatchingTimestamp(timestamps, ts);
        const matchedCurrentTsKey = getTsCacheKey(matchedCurrentTs);

        const rawSearchTs = getSearchParamTs(location.search);
        const matchedSearchTs = findMatchingTimestamp(timestamps, rawSearchTs);
        const matchedSearchTsKey = getTsCacheKey(matchedSearchTs);

        if (!matchedCurrentTsKey && !matchedSearchTsKey) return;
        if (matchedCurrentTsKey === matchedSearchTsKey) return;

        const params = new URLSearchParams(location.search || '');

        if (matchedCurrentTs) {
            params.set('ts', String(matchedCurrentTs));
        } else {
            params.delete('ts');
        }

        history.replace({
            ...location,
            search: params.toString() ? '?' + params.toString() : ''
        });

        lastSyncedSearchTsRef.current = matchedCurrentTsKey || null;
    }, [ts, timestamps, history, location]);

    useEffect(() => {
        if (ts && optimizationPipelineData?.optimizationPipelineLog) {
            optimizationPipelineDetailsRequestRef.current += 1;

            const requestId = optimizationPipelineDetailsRequestRef.current;
            const cacheKey = getTsCacheKey(ts);
            const cached = optimizationPipelineDetailsCacheRef.current[cacheKey];
            const hasCached = Object.prototype.hasOwnProperty.call(optimizationPipelineDetailsCacheRef.current, cacheKey);

            if (hasCached) {
                setOptimizationPipelineDetails(cached);
                setOptimizationPipelineDetailsLoading(false);
                setOptimizationPipelineDetailsLoaded(true);
                return;
            }

            setOptimizationPipelineDetailsLoading(true);

            OptimizationPipelineLogTableService.getData(
                optimizationPipelineData.optimizationPipelineLog,
                ts,
                ts
            )
                .then(resp => {
                    optimizationPipelineDetailsCacheRef.current[cacheKey] = resp;

                    if (requestId !== optimizationPipelineDetailsRequestRef.current) return;

                    setOptimizationPipelineDetails(resp);
                    setOptimizationPipelineDetailsLoading(false);
                    setOptimizationPipelineDetailsLoaded(true);
                })
                .catch(() => {
                    optimizationPipelineDetailsCacheRef.current[cacheKey] = null;

                    if (requestId !== optimizationPipelineDetailsRequestRef.current) return;

                    setOptimizationPipelineDetails(null);
                    setOptimizationPipelineDetailsLoading(false);
                    setOptimizationPipelineDetailsLoaded(true);
                });
        } else {
            optimizationPipelineDetailsRequestRef.current += 1;
            setOptimizationPipelineDetails(null);
            setOptimizationPipelineDetailsLoading(false);
            setOptimizationPipelineDetailsLoaded(false);
        }
    }, [ts, optimizationPipelineData]);

    const optimizationPipelineDetail = Array.isArray(optimizationPipelineDetails) ? optimizationPipelineDetails[0] : optimizationPipelineDetails;

    const value = {
        id,
        ts,
        setTs,
        sidebar,
        openSidebar,
        closeSidebar,
        popup,
        setPopup,
        openPopup,
        closePopup,
        optimizationPipelineData,
        optimizationPipelineDetails,
        optimizationPipelineDetail,
        optimizationPipelineDetailsLoading,
        optimizationPipelineDetailsLoaded,
        timestamps,
        getOnDemandResource,
        ensureOnDemandResource
    };

    return (<OptimizationPipelineContext.Provider value={value}>{props.children}</OptimizationPipelineContext.Provider>);
}
